import { useAnalytics } from "@hooks/useAnalytics";
import { useApp } from "@hooks/useApp";
import { type ComponentProps } from "@ts/components";
import { type CustomImage } from "@ts/sanity";
import { type Article } from "@ts/sanity";
import React, { type FC } from "react";

export type FeaturedArticleCardInputProps = ComponentProps & {
  article: Article;
  dataTestId: string;
  handleClick: () => void;
};

export type FeaturedArticleCardOutputProps = Omit<
  FeaturedArticleCardInputProps,
  "article" | "handleClick"
> & {
  image?: CustomImage;
  title?: string;
  summary?: string;
  link: string;
  handleClick: () => void;
};

export const withFeaturedArticleCard =
  (Component: FC<FeaturedArticleCardOutputProps>) =>
  ({
    name = "FeaturedArticleCard",
    article,
    dataTestId,
    handleClick,
  }: FeaturedArticleCardInputProps) => {
    const {
      config: {
        settings: { routes },
      },
    } = useApp();

    const { trackClick } = useAnalytics();
    const { title, summary } = article || {};
    const link = article?.slug?.current
      ? `${routes[`ARTICLES`]}/${article?.slug?.current}`
      : "/";

    const handleClickTracking = () => {
      handleClick();
      trackClick("Article", link);
    };

    Component.displayName = name;
    return (
      <Component
        image={article?.image}
        title={title}
        summary={summary}
        link={link}
        handleClick={handleClickTracking}
        dataTestId={dataTestId}
      />
    );
  };
